import { boolean, mixed, object, string } from 'yup';

import { ConversationSelectType } from '../../../../../data/data/conversations/enums';
import {
  ConsultType,
  Honorific,
  TemplateFrequency,
} from '../../../../../data/types/api/graphql';
import {
  messages,
  nanNumber,
  trim,
} from '../../../../../data/utils/form.utils';

export const schema = object()
  .shape({
    consultType: mixed<ConsultType>()
      .oneOf(Object.values(ConsultType))
      .required(messages.required.id)
      .default(ConsultType.Physical),
    patientId: string()
      .transform(trim)
      .when('consultType', {
        is: (consultType: ConsultType) => consultType !== ConsultType.Else,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .default(''),
    patientsInAllPractices: boolean().default(false),
    conversationType: mixed<ConversationSelectType>()
      .oneOf(Object.values(ConversationSelectType))
      .required(messages.required.id)
      .default(ConversationSelectType.EXISTING),
    bulkId: string()
      .transform(trim)
      .when(['consultType'], {
        is: (consultType: ConsultType) => consultType === ConsultType.Bulk,
        then: currentSchema => currentSchema.required(messages.required.id),
      }),
    conversationId: string()
      .transform(trim)
      .when(['consultType', 'conversationType'], {
        is: (
          consultType: ConsultType,
          conversationType: ConversationSelectType
        ) =>
          ![ConsultType.Else, ConsultType.Bulk].includes(consultType) &&
          conversationType === ConversationSelectType.EXISTING,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .default(''),
    conversationTitle: string()
      .transform(trim)
      .when(['consultType', 'conversationType'], {
        is: (
          consultType: ConsultType,
          conversationType: ConversationSelectType
        ) =>
          ![ConsultType.Else, ConsultType.Bulk].includes(consultType) &&
          conversationType === ConversationSelectType.NEW,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .default(''),

    title: string()
      .transform(trim)
      .when('consultType', {
        is: (consultType: ConsultType) => consultType === ConsultType.Else,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .default(''),
    healthProfessionalId: string()
      .transform(trim)
      .required(messages.required.id)
      .default(''),

    frequency: mixed<TemplateFrequency>()
      .oneOf(Object.values(TemplateFrequency))
      .required(messages.required.id)
      .default(TemplateFrequency.Once),

    startDate: string()
      .transform(trim)
      .required(messages.required.id)
      .default(''),
    endDate: string()
      .transform(trim)
      .when('frequency', {
        is: (frequency: TemplateFrequency) =>
          frequency !== TemplateFrequency.Once,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .default(''),
    rangeStartDate: string()
      .transform(trim)
      .when('consultType', {
        is: (consultType: ConsultType) => consultType === ConsultType.Else,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .default(''),
    rangeEndDate: string()
      .transform(trim)
      .when('consultType', {
        is: (consultType: ConsultType) => consultType === ConsultType.Else,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .default(''),
    honorific: mixed<Honorific>()
      .oneOf(Object.values(Honorific))
      .when('consultType', {
        is: (consultType: ConsultType) => consultType !== ConsultType.Else,
        then: currentSchema => currentSchema.required(messages.required.id),
      })
      .nullable(),
    irregular: boolean().default(false).nullable(),
    minutes: nanNumber()
      .transform(value => {
        const num = Number(value);
        return Number.isNaN(num) ? undefined : num;
      })
      .when('consultType', {
        is: (consultType: ConsultType) =>
          ![ConsultType.Else, ConsultType.Bulk].includes(consultType),
        then: currentSchema => currentSchema.required(messages.required.id),
      }),
    note: string().transform(trim).default(''),
  })
  .required()
  .noUnknown();
